/**
 * The single edit point for the Compliance-tab deep link (SE-7002, FC-1.1).
 *
 * "View full impact →" must land the reader on the Compliance tab with the
 * scenario already loaded. That tab does not exist yet — SE-7005 is building
 * it and SE-7008 owns the scenario-overlay deep link on the receiving end — so
 * the intent is expressed here, in one small named module, rather than as a
 * literal string scattered through four modals. Pointing it at the real route
 * is then a one-line change in this file.
 *
 * ASSUMPTIONS, to be reconciled against SE-7005 / SE-7008 before release:
 *
 * - the Compliance surface is a sub-tab of the existing `/squad` route, in the
 *   same `?tab=` family as `planning` / `contracts` / `analysis` (the pattern
 *   `SquadScreenContainer` already implements);
 * - its tab value is `compliance`;
 * - a scenario is loaded onto it by a `scenarioId` query parameter.
 *
 * If any of those turn out differently, this file is the only thing that
 * changes.
 * @module features/financial-compliance/navigation/complianceRoute
 */

/** The route that hosts the Compliance tab. */
export const COMPLIANCE_ROUTE = "/squad";

/** The `?tab=` value that selects the Compliance tab (assumed, see above). */
export const COMPLIANCE_TAB_PARAM = "compliance";

/** The query parameter SE-7008 is assumed to read the scenario from. */
export const COMPLIANCE_SCENARIO_PARAM = "scenarioId";

/**
 * Build the href for "View full impact →".
 *
 * A draft scenario has no id yet — it exists only in the planning-mode store —
 * so the scenario parameter is omitted rather than sent as an empty string,
 * which a receiving screen would have to special-case.
 * @param scenarioId - The saved scenario being edited, when there is one.
 * @returns The href to navigate to.
 */
export const buildComplianceHref = (scenarioId?: string | null): string => {
  const base = `${COMPLIANCE_ROUTE}?tab=${COMPLIANCE_TAB_PARAM}`;
  return scenarioId != null && scenarioId !== ""
    ? `${base}&${COMPLIANCE_SCENARIO_PARAM}=${encodeURIComponent(scenarioId)}`
    : base;
};
